import { styled } from '@mui/material';
import { QTypography, QCard, QCardHeader, QCardContent } from '@kw/quokka-ui';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { NumDeviceFailedPolicies } from '@kw/rules-service/dist/src/policy/dtos/NumDeviceFailedPolicies.dto';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useFetchService } from '../../utils/fetchService';
import { PageLoader } from '../../PageLoader';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { LaunchDarklyFlags } from '../../launch-darkly';

function useNumberDevicesFailedPolicies(range: number, accessToken, flags: LaunchDarklyFlags['flags']) {
  const path = `/eval/policies/number-devices-failed-policies?range=${range * 2}`;

  const { data, error } = useFetchService<NumDeviceFailedPolicies[]>(path, accessToken);

  const isLoadingPolicies = !data && !error;

  function averageNumberOfDevicesFailed(devicesData: NumDeviceFailedPolicies[]) {
    return Math.round(
      devicesData.reduce((p, n) => {
        return p + n.numberOfFailedDevices;
      }, 0) / devicesData.length,
    );
  }

  let currentPeriodAvg = 0;
  let percentChange = 0;
  if (!isLoadingPolicies && data) {
    const previousPeriod = data.slice(0, data.length / 2);
    const currentPeriod = data.slice(data.length / 2);
    const previousPeriodAvg = averageNumberOfDevicesFailed(previousPeriod);
    currentPeriodAvg = averageNumberOfDevicesFailed(currentPeriod);

    if (previousPeriodAvg !== 0) {
      percentChange = Math.round(((currentPeriodAvg - previousPeriodAvg) / previousPeriodAvg) * 100.0);
    } else {
      // Technically this is undefined (infinite % increase), but just treating it as 100% for each
      percentChange = currentPeriodAvg * 100.0;
    }
  }

  return { numberDevicesFailedPolicies: currentPeriodAvg, percentChange, error, isLoadingPolicies };
}

export function FailedDeviceStatCard({ initialDateRange }: { initialDateRange: number }) {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const flags: LaunchDarklyFlags['flags'] = useFlags();
  const {
    numberDevicesFailedPolicies = 0,
    percentChange = 0,
    isLoadingPolicies,
  } = useNumberDevicesFailedPolicies(initialDateRange, accessToken, flags);

  const TrendingLine = ({ value }: { value: number }) => {
    if (value > 0) {
      return <StyledTrendingUpIcon color="warning" />;
    }

    if (value === 0) {
      return <StyledTrendingFlatIcon color="info" />;
    }

    return <StyledTrendingDownIcon color="success" />;
  };

  const Loader = () => {
    if (isLoadingPolicies) {
      return <PageLoader />;
    }

    return null;
  };

  return (
    <QCard>
      <Loader />
      <QCardHeader title={<QTypography variant="h4Medium">Average Device Violations per Day</QTypography>}></QCardHeader>
      <StyledQCardContent>
        <div style={{ fontSize: '60px', marginRight: '20px' }}>
          {numberDevicesFailedPolicies}
          <div style={{ width: '100%' }} />
          <div style={{ fontSize: '18px' }}>Devices</div>
        </div>
        <TrendingLine value={Number(percentChange)} />
        <QTypography variant="h5Medium">
          {percentChange}% from last {initialDateRange} days
        </QTypography>
      </StyledQCardContent>
    </QCard>
  );
}

const StyledQCardContent = styled(QCardContent)({
  lineHeight: '53px',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
});

const StyledTrendingUpIcon = styled(TrendingUp)({
  fontSize: '30px',
  marginRight: '10px',
});

const StyledTrendingDownIcon = styled(TrendingDown)({ fontSize: '30px', marginRight: '10px' });

const StyledTrendingFlatIcon = styled(TrendingFlat)({
  fontSize: '30px',
  marginRight: '10px',
});
